import React from 'react'
import { LuCheck, LuClock, LuX } from 'react-icons/lu'
import { useNavigate } from 'react-router'

import CompanyAvatar from '@/components/misc/CompanyAvatar'
import SearchResultItem from '@/components/misc/GlobalSearch/components/SearchResultItem'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import { SearchResult } from '@/components/misc/GlobalSearch/types'
import { Badge } from '@/components/ui/badge'
import { CommandGroup } from '@/components/ui/command'
import { PANEL_STATUS } from '@/lib/constants'
import globalSearchService from '@/lib/services/globalSearchService'
import { RegisteredCompanySearchResult } from '@/types/responses/companies'

interface Props {
  companies: SearchResult[]
  /** Starting pop-in index, so companies animate in after the referrals. */
  indexOffset?: number
}

const CompanyResults: React.FC<Props> = ({ companies, indexOffset = 0 }) => {
  const globalSearch = useGlobalSearch()
  const navigate = useNavigate()

  if (companies.length === 0) {
    return null
  }

  return (
    <CommandGroup heading="Companies">
      {companies.map((company, index) => (
        <SearchResultItem
          key={company['@id']}
          index={indexOffset + index}
          value={company['@id']}
          className="gap-3 items-start"
          onSelect={() => {
            globalSearch.hide()
            navigate(globalSearchService.getCompanyUrl(company))
          }}
        >
          <CompanyAvatar
            size="lg"
            tooltip={false}
            company={{
              presentationName: getCompanyName(company),
              iconUrl: getCompanyIconUrl(company),
            }}
          />

          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium">
              {getCompanyName(company)}
            </span>

            {getCompanySubtext(company)}
          </div>
        </SearchResultItem>
      ))}
    </CommandGroup>
  )
}

function getCompanyName(company: SearchResult): string {
  if (globalSearchService.isRegisteredCompany(company)) {
    return company.presentationName
  }

  if (globalSearchService.isUnregisteredCompany(company)) {
    return company.name
  }

  return company.companyName
}

function getCompanyIconUrl(company: SearchResult): string | null {
  return globalSearchService.isRegisteredCompany(company)
    ? company.iconUrl
    : null
}

function getCompanySubtext(company: SearchResult): React.ReactNode {
  if (globalSearchService.isUnregisteredCompany(company)) {
    return <Badge variant="secondary">Not registered on RQ</Badge>
  }

  if (globalSearchService.isPanelInviteCompany(company)) {
    return (
      <Badge variant="warning-light" size="sm">
        <LuClock className="size-3.5 text-warning" /> Invitation sent
      </Badge>
    )
  }

  const typeName = company.companyType?.name

  return (
    <Subtext>
      {typeName && typeName}

      {getPanelStatus(company.panelWithCurrentCompany)}
    </Subtext>
  )
}

/**
 * The company's relationship with the current company. Keeps its own semantic
 * colour so it stands out from the muted company type shown before it.
 */
function getPanelStatus(
  panel: RegisteredCompanySearchResult['panelWithCurrentCompany'] | null,
): React.ReactNode {
  if (!panel) {
    return (
      <Badge variant="primary-light" size="sm">
        On RQ
      </Badge>
    )
  }

  if (panel.status === PANEL_STATUS.approved) {
    return (
      <Badge variant="success-light" size="sm">
        <LuCheck className="size-3.5 text-success" /> In your professional
        network
      </Badge>
    )
  }

  if (panel.status === PANEL_STATUS.requested) {
    return (
      <Badge variant="warning-light" size="sm">
        <LuClock className="size-3.5 text-warning-dark" /> Relationship request
        pending
      </Badge>
    )
  }

  return (
    <Badge variant="destructive-light" size="sm">
      <LuX className="size-3.5 text-destructive" /> Relationship request
      rejected
    </Badge>
  )
}

const Subtext: React.FC<React.ComponentProps<'span'>> = ({
  className,
  children,
}) => (
  <span
    className={`text-muted-foreground flex flex-col items-start text-xs gap-1 ${
      className ?? ''
    }`}
  >
    {children}
  </span>
)

export default CompanyResults
